// Путь назад к папкам (жалоба владельца 24.10.2026: «непонятно, как из открытого документа
// перейти обратно в браузер документов и папок»): крошки шапки листа «Документы / папка / … /
// протокол», как «Настройки / Пользователи» у остальных экранов, но звенья — ссылки, как
// адресная строка Проводника. «Документы» и каждая папка ведут в «Документы» с этой папкой
// и выделенным протоколом (?folder=…&select=…); корень — только ?select=…. Узел протокола
// в менеджере — тот же id, что у документа и маршрута протокола.
//
// Папки — из списка менеджера тем же запросом, что у «Документов» (manager.list, общий кэш);
// модуль «Документов» не импортируется (границы модулей, pnpm deps). Пока список грузится,
// не пришёл или протокола в нём нет — одно звено «Документы». На телефоне путь сокращается до
// одной ссылки назад — на папку протокола («‹ Строительство»), без горизонтальной прокрутки.
//
// Строка документа (ux-dense, 25.10.2026): шире 640 px последнее звено — заголовок страницы
// (current: h1 «П-N · название»), за путём — пометки и «О протоколе» (after); звенья — через «›».
//
// 022 (T029; research Р11): без documents.read (внешний пользователь) «Документов» нет — список
// менеджера не запрашивается, путь «Главная › протокол», на телефоне — «‹ Главная»: главная
// внешнего — «Открытые вам протоколы». Сопровождение по временному доступу (правило grant)
// «Документы» открывает — у него путь прежний.
import type { DocumentManagerNode } from '@kp/contracts';
import { Icon, cx, useIsPhone } from '@kp/ui';
import { ChevronLeft } from '@kp/ui/icons';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { apiQuery } from '../../../api/client.ts';
import { useCrumbSlot } from '../../../shell/context.tsx';
import { can } from '../../../shell/permissions.ts';
import { useMe } from '../../../shell/session.ts';

export interface PathFolder {
  readonly id: string;
  readonly title: string;
}

type PathNode = Pick<DocumentManagerNode, 'id' | 'kind' | 'parentId' | 'title'>;

/**
 * Папки от корня до папки узла id. Узла нет в списке — null; папку выше не видно (нет прав) —
 * путь начинается с первой видимой. Цикл в данных путь не зацикливает.
 */
export function folderPath(nodes: readonly PathNode[], id: string): PathFolder[] | null {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const node = byId.get(id);
  if (!node) return null;
  const path: PathFolder[] = [];
  const seen = new Set<string>([id]);
  let parent = node.parentId;
  while (parent !== null && !seen.has(parent)) {
    seen.add(parent);
    const folder = byId.get(parent);
    if (!folder || folder.kind !== 'folder') break;
    path.unshift({ id: folder.id, title: folder.title });
    parent = folder.parentId;
  }
  return path;
}

/**
 * Параметры «Документов» для звена пути: папка (корень — без неё) и протокол, который там
 * выделить. Тип — как у маршрута «Документов» до select: параметр читает дорожка «Документов».
 */
export function documentsSearch(folderId: string | null, protocolId: string): { folder?: string } {
  const search: { folder?: string; select: string } = {
    ...(folderId ? { folder: folderId } : {}),
    select: protocolId,
  };
  return search;
}

export interface ProtocolPlace {
  /** Папки от корня до папки протокола; список не пришёл — пусто. */
  folders: PathFolder[];
  /** Папка протокола; корень или список не пришёл — null. */
  parent: PathFolder | null;
  /** Название из списка менеджера — до ответа состояния протокола. */
  listedTitle: string | null;
}

/** «Документы» есть у пользователя (documents.read с любым правилом); у внешнего — нет. */
export function useOpensDocuments(): boolean {
  return can(useMe().permissions, ['documents.read']);
}

/**
 * Место протокола в «Документах» — из списка менеджера (общий кэш с «Документами»): путь,
 * ссылка «Документ в «Документах»» в «О протоколе». enabled — только когда путь на экране.
 * Без «Документов» (022: внешний) список не запрашивается: сервер ответил бы отказом.
 */
export function useProtocolPlace(id: string, enabled: boolean): ProtocolPlace {
  const documents = useOpensDocuments();
  const list = useQuery({ ...apiQuery('manager.list'), enabled: enabled && documents });
  const items = list.data?.items ?? [];
  const folders = folderPath(items, id) ?? [];
  return {
    folders,
    parent: folders.at(-1) ?? null,
    listedTitle: items.find((node) => node.id === id)?.title ?? null,
  };
}

export interface ProtocolPathProps {
  /** Протокол: id документа = id узла менеджера. */
  id: string;
  /** Название протокола; до ответа — из списка менеджера. */
  title: string | null;
  /** Последнее звено вместо названия текстом: заголовок страницы (строка документа). */
  current?: ReactNode;
  /** За путём: пометки и «О протоколе» (строка документа). */
  after?: ReactNode;
}

/** Путь протокола в крошках шапки; вне каркаса (нет слота) — ничего. */
export function ProtocolPath({ id, title, current, after }: ProtocolPathProps) {
  const slot = useCrumbSlot();
  const phone = useIsPhone();
  const documents = useOpensDocuments();
  const { folders, parent, listedTitle } = useProtocolPlace(id, slot !== null);
  if (!slot) return null;
  const name = title?.trim() || listedTitle || 'Таблица протокола';
  return createPortal(
    <>
      <nav aria-label="Путь" className={cx('kp-pt-path', !phone && 'is-doc')}>
        {phone ? (
          <ol>
            <li>
              {documents ? (
                <Link
                  to="/documents"
                  search={documentsSearch(parent?.id ?? null, id)}
                  className="kp-pt-crumb kp-pt-crumb-back"
                >
                  <Icon icon={ChevronLeft} />
                  <span>{parent?.title ?? 'Документы'}</span>
                </Link>
              ) : (
                <Link to="/" className="kp-pt-crumb kp-pt-crumb-back">
                  <Icon icon={ChevronLeft} />
                  <span>Главная</span>
                </Link>
              )}
            </li>
          </ol>
        ) : (
          <ol>
            <li className="kp-pt-path-root">
              {documents ? (
                <Link to="/documents" search={documentsSearch(null, id)} className="kp-pt-crumb">
                  Документы
                </Link>
              ) : (
                <Link to="/" className="kp-pt-crumb">
                  Главная
                </Link>
              )}
            </li>
            {folders.map((folder) => (
              <li
                key={folder.id}
                className={cx('kp-pt-path-folder', folder === parent && 'is-parent')}
              >
                <Link
                  to="/documents"
                  search={documentsSearch(folder.id, id)}
                  className="kp-pt-crumb"
                  title={folder.title}
                >
                  {folder.title}
                </Link>
              </li>
            ))}
            <li
              aria-current="page"
              className="kp-pt-path-current"
              {...(current == null ? { title: name } : {})}
            >
              {current ?? name}
            </li>
          </ol>
        )}
      </nav>
      {phone ? null : after}
    </>,
    slot,
  );
}
